import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, Save, Loader2, Lock, ChevronRight, ChevronDown } from "lucide-react";
import {
  type SkillV19Row,
  type SkillV19Input,
  type IntentSignature,
  type PreCondition,
  type PostCondition,
  type ModelPreference,
  useCreateSkillV19,
  useUpdateSkillV19,
} from "@/hooks/useSkillsV19";
import { SKILL_TEMPLATES, SKILL_TEMPLATE_ORDER, type SkillTemplateKey } from "./skillTemplates";
import { useSkillFormValidation, slugify } from "./useSkillFormValidation";

const PRE_OPS: PreCondition["op"][] = ["eq", "neq", "gt", "gte", "lt", "lte", "in", "exists", "not_exists"];
const POST_CHECKS: PostCondition["check"][] = ["contains", "not_contains", "matches", "min_len", "max_len", "json_has"];

const MODELS = [
  { provider: "google", model: "google/gemini-2.5-flash" },
  { provider: "google", model: "google/gemini-2.5-flash-lite" },
  { provider: "google", model: "google/gemini-2.5-pro" },
  { provider: "openai", model: "openai/gpt-5-mini" },
  { provider: "openai", model: "openai/gpt-5" },
  { provider: "openai", model: "openai/gpt-5-nano" },
];

const IMMUTABLE_FIELDS = [
  "prompt_template",
  "intent_signature",
  "pre_conditions",
  "post_conditions",
  "tools_allowed",
  "model_preference",
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill: SkillV19Row | null;
  defaultOrgId: string | null;
}

function emptyInput(orgId: string | null): SkillV19Input {
  return {
    slug: "",
    org_id: orgId,
    group_id: null,
    name: "",
    description: "",
    status: "shadow",
    intent_signature: { primary_intent: null, secondary_intents: [], excluded_intents: [] },
    context_features: {},
    pre_conditions: [],
    post_conditions: [],
    prompt_template: "",
    tools_allowed: [],
    model_preference: null,
    refusal_policy: null,
    telemetry_config: {},
    created_by: null,
    notes: null,
    version: 1,
  };
}

function inferTemplateFromSkill(skill: SkillV19Row | null): SkillTemplateKey {
  if (!skill) return "" as SkillTemplateKey;
  const intent = skill.intent_signature?.primary_intent ?? "";
  const match = SKILL_TEMPLATE_ORDER.find((k) => SKILL_TEMPLATES[k].intent_primary && SKILL_TEMPLATES[k].intent_primary === intent);
  return match ?? "custom";
}

export function SkillV19FormDialog({ open, onOpenChange, skill, defaultOrgId }: Props) {
  const { toast } = useToast();
  const create = useCreateSkillV19();
  const update = useUpdateSkillV19();

  const [form, setForm] = useState<SkillV19Input>(emptyInput(defaultOrgId));
  const [type, setType] = useState<SkillTemplateKey | "">("");
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const isEdit = !!skill;
  const lockedImmutables = isEdit && skill!.status === "deprecated";

  useEffect(() => {
    if (!open) return;
    if (skill) {
      setForm({
        slug: skill.slug,
        org_id: skill.org_id,
        group_id: skill.group_id,
        name: skill.name,
        description: skill.description,
        status: skill.status,
        intent_signature: skill.intent_signature,
        context_features: skill.context_features,
        pre_conditions: skill.pre_conditions,
        post_conditions: skill.post_conditions,
        prompt_template: skill.prompt_template,
        tools_allowed: skill.tools_allowed,
        model_preference: skill.model_preference,
        refusal_policy: skill.refusal_policy,
        telemetry_config: skill.telemetry_config,
        created_by: skill.created_by,
        notes: skill.notes,
        version: skill.version,
      });
      setType(inferTemplateFromSkill(skill));
    } else {
      setForm(emptyInput(defaultOrgId));
      setType("");
    }
    setAdvancedOpen(false);
  }, [open, skill, defaultOrgId]);

  // Patterns derived from context_features.patterns
  const patterns = useMemo(() => {
    const cf = (form.context_features ?? {}) as Record<string, unknown>;
    const p = (cf.patterns as { pt_br?: string[]; en?: string[] } | undefined) ?? {};
    return { pt_br: p.pt_br ?? [], en: p.en ?? [] };
  }, [form.context_features]);

  const setPatterns = (next: { pt_br?: string[]; en?: string[] }) => {
    setForm((f) => ({
      ...f,
      context_features: {
        ...(f.context_features ?? {}),
        patterns: { pt_br: next.pt_br ?? patterns.pt_br, en: next.en ?? patterns.en },
      },
    }));
  };

  const validation = useSkillFormValidation({
    name: form.name,
    slug: form.slug,
    description: form.description,
    type,
    intent_primary: form.intent_signature.primary_intent ?? "",
    patterns_pt_br: patterns.pt_br,
    patterns_en: patterns.en,
    prompt_template: form.prompt_template,
    isEdit,
    currentId: skill?.id ?? null,
  });
  const { errors, hasErrors, slugChecking } = validation;

  const applyTemplate = (key: SkillTemplateKey) => {
    setType(key);
    const tpl = SKILL_TEMPLATES[key];
    setForm((f) => ({
      ...f,
      intent_signature: {
        ...f.intent_signature,
        primary_intent: tpl.intent_primary || null,
      },
      context_features: {
        ...(f.context_features ?? {}),
        patterns: { pt_br: tpl.patterns_pt_br, en: tpl.patterns_en },
      },
      prompt_template: tpl.prompt_template,
    }));
  };

  const onNameChange = (name: string) => {
    setForm((f) => {
      const next = { ...f, name };
      if (!isEdit && (!f.slug || f.slug === slugify(f.name))) {
        next.slug = slugify(name);
      }
      return next;
    });
  };

  const onSubmit = async () => {
    if (hasErrors) {
      toast({ title: "Verifique os campos destacados", variant: "destructive" });
      return;
    }
    try {
      if (isEdit) {
        const patch: Partial<SkillV19Input> = lockedImmutables
          ? {
              name: form.name,
              description: form.description,
              notes: form.notes,
              telemetry_config: form.telemetry_config,
              context_features: form.context_features,
              refusal_policy: form.refusal_policy,
            }
          : form;
        await update.mutateAsync({ id: skill!.id, patch });
        toast({ title: "Skill atualizada", description: `${form.slug} v${skill!.version}` });
      } else {
        await create.mutateAsync(form);
        toast({ title: "Skill criada", description: `${form.slug} (shadow)` });
      }
      onOpenChange(false);
    } catch (err) {
      const e = err as Record<string, unknown> | null;
      const msg =
        (e?.message as string | undefined) ??
        (e?.error_description as string | undefined) ??
        (e?.details as string | undefined) ??
        (e?.hint as string | undefined) ??
        (typeof err === "string" ? err : JSON.stringify(err));
      toast({ title: "Erro ao salvar", description: String(msg), variant: "destructive" });
    }
  };

  const sig = form.intent_signature;
  const setSig = (next: Partial<IntentSignature>) =>
    setForm((f) => ({ ...f, intent_signature: { ...f.intent_signature, ...next } }));

  const saving = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? `Editar ${skill!.slug}` : "Nova Skill"}
            {isEdit && <Badge variant="secondary" className="text-xs">v{skill!.version}</Badge>}
            {isEdit && <Badge>{skill!.status}</Badge>}
          </DialogTitle>
          <DialogDescription>
            Memória procedural. Slug é único por escopo.
          </DialogDescription>
        </DialogHeader>

        {lockedImmutables && (
          <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-xs">
            <Lock className="h-4 w-4 mt-0.5 text-warning shrink-0" />
            <div>
              <strong>Status {skill!.status}:</strong> campos imutáveis bloqueados
              ({IMMUTABLE_FIELDS.join(", ")}). Para alterar, faça <em>fork</em> de uma nova versão.
            </div>
          </div>
        )}

        {/* ESSENCIAL */}
        <div className="space-y-4">
          <div>
            <Label>Tipo *</Label>
            <Select value={type} onValueChange={(v) => applyTemplate(v as SkillTemplateKey)} disabled={isEdit}>
              <SelectTrigger>
                <SelectValue placeholder="Escolha um tipo de skill..." />
              </SelectTrigger>
              <SelectContent>
                {SKILL_TEMPLATE_ORDER.map((k) => (
                  <SelectItem key={k} value={k}>{SKILL_TEMPLATES[k].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.type && <p className="text-xs text-destructive mt-1">{errors.type}</p>}
            {!isEdit && type && type !== "custom" && (
              <p className="text-xs text-muted-foreground mt-1">
                Template prefilou intent + padrões + prompt. Ajuste no bloco Avançado se quiser.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Resumir conversa"
                maxLength={120}
              />
              {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label>Slug *</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                placeholder="resumir-conversa"
                disabled={isEdit}
                maxLength={64}
              />
              {errors.slug && <p className="text-xs text-destructive mt-1">{errors.slug}</p>}
              {slugChecking && !errors.slug && (
                <p className="text-xs text-muted-foreground mt-1">Verificando disponibilidade...</p>
              )}
            </div>
          </div>

          <div>
            <Label>Descrição *</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              maxLength={500}
              placeholder="O que essa skill faz e quando é útil."
            />
            {errors.description && <p className="text-xs text-destructive mt-1">{errors.description}</p>}
          </div>

          <div className="space-y-3 border rounded-md p-3 bg-muted/30">
            <Label className="text-sm font-semibold">Quando dispara</Label>
            <div>
              <Label className="text-xs">Intent primária</Label>
              <Input
                value={sig.primary_intent ?? ""}
                onChange={(e) => setSig({ primary_intent: e.target.value || null })}
                placeholder="ex: summary_request"
                disabled={lockedImmutables}
              />
            </div>
            <TagListEditor
              label="Padrões PT-BR (palavras/expressões)"
              items={patterns.pt_br}
              onChange={(items) => setPatterns({ pt_br: items })}
              placeholder="ex: resuma"
              disabled={lockedImmutables}
            />
            <TagListEditor
              label="Padrões EN"
              items={patterns.en}
              onChange={(items) => setPatterns({ en: items })}
              placeholder="ex: summarize"
              disabled={lockedImmutables}
            />
            {errors.intent_or_patterns && (
              <p className="text-xs text-destructive">{errors.intent_or_patterns}</p>
            )}
          </div>
        </div>

        {/* AVANCADO */}
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="mt-4">
          <CollapsibleTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="w-full justify-start">
              {advancedOpen ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
              {advancedOpen ? "Ocultar avançado" : "Mostrar avançado"}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-3">
            <TagListEditor
              label="Intents secundárias (peso 0.7)"
              items={sig.secondary_intents}
              onChange={(items) => setSig({ secondary_intents: items })}
              placeholder="ex: question"
              disabled={lockedImmutables}
            />
            <TagListEditor
              label="Intents excluídas (descarta a skill)"
              items={sig.excluded_intents}
              onChange={(items) => setSig({ excluded_intents: items })}
              placeholder="ex: complaint"
              disabled={lockedImmutables}
            />
            <JsonFieldEditor
              label="Context features extras (signal → expected)"
              value={(() => {
                const cf = { ...(form.context_features ?? {}) } as Record<string, unknown>;
                delete cf.patterns;
                return cf;
              })()}
              onChange={(v) =>
                setForm({
                  ...form,
                  context_features: { ...(v ?? {}), patterns: patterns },
                })
              }
              placeholder='{"iw.state": "active", "tom.confidence": 0.6}'
            />
            <PreConditionEditor
              items={form.pre_conditions}
              onChange={(v) => setForm({ ...form, pre_conditions: v })}
              disabled={lockedImmutables}
            />
            <PostConditionEditor
              items={form.post_conditions}
              onChange={(v) => setForm({ ...form, post_conditions: v })}
              disabled={lockedImmutables}
            />
            <div>
              <Label>Prompt template{type === "custom" ? " *" : ""}</Label>
              <Textarea
                value={form.prompt_template}
                onChange={(e) => setForm({ ...form, prompt_template: e.target.value })}
                rows={10}
                placeholder={`Você é a SYRA, especialista em {{topic}}.\n\nMensagem do usuário: {{message}}\n\nResponda em PT-BR, máx. 400 chars.`}
                className="font-mono text-xs"
                disabled={lockedImmutables}
              />
              {errors.prompt && <p className="text-xs text-destructive mt-1">{errors.prompt}</p>}
              <p className="text-xs text-muted-foreground mt-1">
                Use {`{{var}}`} para placeholders. Disponíveis: <code>message</code>, <code>group_name</code>,{" "}
                <code>history</code>, <code>signals.*</code>.
              </p>
            </div>
            <ModelPreferenceEditor
              value={form.model_preference}
              onChange={(v) => setForm({ ...form, model_preference: v })}
              disabled={lockedImmutables}
            />
            <TagListEditor
              label="Tools permitidas"
              items={form.tools_allowed}
              onChange={(items) => setForm({ ...form, tools_allowed: items })}
              placeholder="web_search, get_member_info..."
              disabled={lockedImmutables}
            />
            <JsonFieldEditor
              label="Refusal policy"
              value={form.refusal_policy}
              onChange={(v) => setForm({ ...form, refusal_policy: v })}
              placeholder='{"refuse_if": ["pii", "violence"], "fallback_message": "..."}'
              allowNull
            />
            <JsonFieldEditor
              label="Telemetry config"
              value={form.telemetry_config}
              onChange={(v) => setForm({ ...form, telemetry_config: v })}
              placeholder='{"sample_rate": 1.0}'
            />
            <div>
              <Label>Notas internas</Label>
              <Textarea
                value={form.notes ?? ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value || null })}
                rows={2}
                placeholder="Contexto interno, links de PR, etc."
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={saving || hasErrors}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {isEdit ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------- Sub-editors ----------------- */

function TagListEditor({
  label, items, onChange, placeholder, disabled,
}: {
  label: string;
  items: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v || items.includes(v)) return;
    onChange([...items, v]);
    setDraft("");
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 mt-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
        />
        <Button type="button" variant="outline" onClick={add} disabled={disabled || !draft.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {items.map((it) => (
            <Badge key={it} variant="secondary" className="gap-1">
              {it}
              {!disabled && (
                <button
                  onClick={() => onChange(items.filter((x) => x !== it))}
                  className="ml-1 hover:text-destructive"
                  type="button"
                >
                  ×
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function JsonFieldEditor({
  label, value, onChange, placeholder, allowNull,
}: {
  label: string;
  value: unknown;
  onChange: (v: any) => void;
  placeholder?: string;
  allowNull?: boolean;
}) {
  const [text, setText] = useState(() =>
    value == null ? (allowNull ? "" : "{}") : JSON.stringify(value, null, 2)
  );
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setText(value == null ? (allowNull ? "" : "{}") : JSON.stringify(value, null, 2));
  }, [value, allowNull]);

  const onBlur = () => {
    if (!text.trim()) {
      if (allowNull) {
        setErr(null);
        onChange(null);
        return;
      }
      onChange({});
      setErr(null);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      onChange(parsed);
      setErr(null);
    } catch (e) {
      setErr("JSON inválido");
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={onBlur}
        rows={4}
        placeholder={placeholder}
        className="font-mono text-xs"
      />
      {err && <p className="text-xs text-destructive mt-1">{err}</p>}
    </div>
  );
}

function PreConditionEditor({
  items, onChange, disabled,
}: {
  items: PreCondition[];
  onChange: (v: PreCondition[]) => void;
  disabled?: boolean;
}) {
  const update = (i: number, patch: Partial<PreCondition>) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Pré-condições (avaliadas antes do executor)</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...items, { key: "", op: "exists" }])}
        >
          <Plus className="h-3 w-3 mr-1" /> Adicionar
        </Button>
      </div>
      {items.length === 0 && (
        <p className="text-xs text-muted-foreground">Sem pré-condições. Skill sempre passa o gate.</p>
      )}
      {items.map((p, i) => {
        const needsValue = !["exists", "not_exists"].includes(p.op);
        return (
          <div key={i} className="grid grid-cols-12 gap-2 items-start border rounded-md p-2">
            <div className="col-span-4">
              <Input
                placeholder="iw.state"
                value={p.key}
                onChange={(e) => update(i, { key: e.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="col-span-3">
              <Select value={p.op} onValueChange={(v) => update(i, { op: v as PreCondition["op"] })} disabled={disabled}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRE_OPS.map((op) => (
                    <SelectItem key={op} value={op}>{op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-4">
              {needsValue && (
                <Input
                  placeholder="valor (json p/ in)"
                  value={typeof p.value === "string" ? p.value : JSON.stringify(p.value ?? "")}
                  onChange={(e) => {
                    let v: unknown = e.target.value;
                    try {
                      v = JSON.parse(e.target.value);
                    } catch {
                      /* keep string */
                    }
                    update(i, { value: v });
                  }}
                  disabled={disabled}
                />
              )}
            </div>
            <div className="col-span-1 flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onChange(items.filter((_, idx) => idx !== i))}
                disabled={disabled}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PostConditionEditor({
  items, onChange, disabled,
}: {
  items: PostCondition[];
  onChange: (v: PostCondition[]) => void;
  disabled?: boolean;
}) {
  const update = (i: number, patch: Partial<PostCondition>) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Pós-condições (validam saída do modelo)</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...items, { check: "min_len", target: "1" }])}
        >
          <Plus className="h-3 w-3 mr-1" /> Adicionar
        </Button>
      </div>
      {items.length === 0 && (
        <p className="text-xs text-muted-foreground">Sem pós-condições. Saída aceita como veio.</p>
      )}
      {items.map((p, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-start border rounded-md p-2">
          <div className="col-span-4">
            <Select value={p.check} onValueChange={(v) => update(i, { check: v as PostCondition["check"] })} disabled={disabled}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {POST_CHECKS.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-7">
            <Input
              placeholder="target (texto, regex ou número)"
              value={p.target}
              onChange={(e) => update(i, { target: e.target.value })}
              disabled={disabled}
            />
          </div>
          <div className="col-span-1 flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              disabled={disabled}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ModelPreferenceEditor({
  value, onChange, disabled,
}: {
  value: ModelPreference | null;
  onChange: (v: ModelPreference | null) => void;
  disabled?: boolean;
}) {
  const v = value ?? {};
  const set = (patch: Partial<ModelPreference>) => onChange({ ...v, ...patch });
  return (
    <div className="space-y-2 border rounded-md p-3">
      <div className="flex items-center justify-between">
        <Label>Preferência de modelo</Label>
        {value && (
          <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)} disabled={disabled}>
            Limpar
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Modelo</Label>
          <Select
            value={v.model ?? ""}
            onValueChange={(model) => {
              const found = MODELS.find((m) => m.model === model);
              set({ model, provider: found?.provider });
            }}
            disabled={disabled}
          >
            <SelectTrigger><SelectValue placeholder="default (router)" /></SelectTrigger>
            <SelectContent>
              {MODELS.map((m) => (
                <SelectItem key={m.model} value={m.model}>{m.model}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Temperature</Label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={v.temperature ?? ""}
            onChange={(e) => set({ temperature: e.target.value === "" ? undefined : Number(e.target.value) })}
            disabled={disabled}
          />
        </div>
        <div>
          <Label className="text-xs">Max tokens</Label>
          <Input
            type="number"
            min="1"
            value={v.max_tokens ?? ""}
            onChange={(e) => set({ max_tokens: e.target.value === "" ? undefined : Number(e.target.value) })}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}
